import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudDownload as DownloadIcon,
  Language as GlobeIcon,
  Link as LinkIcon,
  Clear as ClearIcon,
  CheckCircleOutline as SuccessIcon,
  Search as SearchIcon,
  Star as StarIcon,
  OpenInNew as OpenInNewIcon,
  MusicNote as MusicNoteIcon,
  PlaylistAddCheck as BatchImportIcon,
  HelpOutline as UnknownSourceIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SongModel } from "../Entities/SongModel";
import {
  ExternalSearchResult,
  apiBatchScrapeUrls,
  apiScrapeUrl,
  apiSearchExternalSongs,
} from "../utils/useDb";

interface ScrapeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (song: SongModel) => void;
  initialQuery?: string;
}

const SUPPORTED_DOMAINS = [
  {
    name: "Tab4U",
    domain: "tab4u.com",
    example: "https://www.tab4u.com/tabs/songs/...",
    color: "#2563eb",
  },
  {
    name: "Ultimate Guitar",
    domain: "ultimate-guitar.com",
    example: "https://tabs.ultimate-guitar.com/tab/...",
    color: "#d97706",
  },
];

function formatTruncatedUrl(rawUrl: string, maxLen: number = 36): string {
  try {
    const u = new URL(rawUrl);
    const pathAndQuery = u.pathname + u.search;
    if (pathAndQuery.length > maxLen) {
      return `...${pathAndQuery.slice(-maxLen)}`;
    }
    return pathAndQuery;
  } catch {
    if (rawUrl.length > maxLen) {
      return `...${rawUrl.slice(-maxLen)}`;
    }
    return rawUrl;
  }
}

export default function ScrapeModal({
  open,
  onClose,
  onSuccess,
  initialQuery,
}: ScrapeModalProps) {
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Search tab states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ExternalSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Direct URL tab states
  const [url, setUrl] = useState<string>("");

  // Import states
  const [importingUrls, setImportingUrls] = useState<Record<string, boolean>>({});
  const [importedSongs, setImportedSongs] = useState<Record<string, SongModel>>({});
  const [isBatchImporting, setIsBatchImporting] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successSong, setSuccessSong] = useState<SongModel | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  const isAnyImporting = Object.values(importingUrls).some(Boolean);

  useEffect(() => {
    if (open && initialQuery) {
      const trimmed = initialQuery.trim();
      const isUrl =
        /^https?:\/\//i.test(trimmed) ||
        SUPPORTED_DOMAINS.some((d) => trimmed.toLowerCase().includes(d.domain));
      if (isUrl) {
        setUrl(trimmed);
        setTabIndex(1);
        setSearchQuery("");
      } else {
        setSearchQuery(trimmed);
        setTabIndex(0);
        setUrl("");
      }
    }
  }, [open, initialQuery]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (isAnyImporting || isBatchImporting) return;
    setTabIndex(newValue);
    setError(null);
  };

  const getDetectedSource = (targetUrl: string) => {
    const trimmed = targetUrl.trim().toLowerCase();
    if (!trimmed) return null;
    if (trimmed.includes("tab4u.com")) {
      return { name: "Tab4U", color: "#2563eb", isKnown: true };
    }
    if (trimmed.includes("ultimate-guitar.com")) {
      return { name: "Ultimate Guitar", color: "#d97706", isKnown: true };
    }
    return { name: "Unknown source", color: "#64748b", isKnown: false };
  };

  const detectedSource = getDetectedSource(url);

  const handleClose = () => {
    if (isAnyImporting || isSearching || isBatchImporting) return;
    setUrl("");
    setSearchQuery("");
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
    setSearchError(null);
    setSuccessSong(null);
    setBatchProgress(null);
    onClose();
  };

  const handleSearch = async (queryOverride?: string) => {
    const term = (queryOverride !== undefined ? queryOverride : searchQuery).trim();
    if (!term) return;

    if (queryOverride !== undefined) {
      setSearchQuery(queryOverride);
    }

    setIsSearching(true);
    setSearchError(null);
    setError(null);
    setHasSearched(true);

    try {
      const results = await apiSearchExternalSongs(term);
      setSearchResults(results);
    } catch (err: any) {
      console.error("External search failed:", err);
      setSearchError(
        err.message || "Failed to search online chord sources. Please try again."
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSearching && searchQuery.trim()) {
      handleSearch();
    }
  };

  const handleImportSong = async (targetUrl: string) => {
    let cleanUrl = targetUrl.trim();
    if (!cleanUrl || importingUrls[cleanUrl] || isBatchImporting) return;

    if (!/^https?:\/\//i.test(cleanUrl)) {
      cleanUrl = "https://" + cleanUrl;
    }

    setImportingUrls((prev) => ({ ...prev, [cleanUrl]: true }));
    setError(null);

    try {
      const scrapedSong = await apiScrapeUrl(cleanUrl);
      setSuccessSong(scrapedSong);
      setImportedSongs((prev) => ({
        ...prev,
        [cleanUrl]: scrapedSong,
        [targetUrl.trim()]: scrapedSong,
      }));
      setToastMessage(`Imported "${scrapedSong.title}" successfully!`);

      // Update search result item's existing_id so button becomes "Open"
      setSearchResults((prev) =>
        prev.map((item) =>
          item.url === targetUrl.trim()
            ? { ...item, existing_id: scrapedSong.id }
            : item
        )
      );

      if (onSuccess) {
        onSuccess(scrapedSong);
      }
    } catch (err: any) {
      console.error("Import failed:", err);
      setError(
        err.message || "Failed to scrape chord page. Please try another link or version."
      );
    } finally {
      setImportingUrls((prev) => ({ ...prev, [cleanUrl]: false }));
    }
  };

  const handleImportAll = async () => {
    if (searchResults.length === 0 || isBatchImporting || isAnyImporting) return;

    setIsBatchImporting(true);
    setError(null);
    setBatchProgress({ current: 0, total: searchResults.length });

    const urls = searchResults.map((r) => r.url);
    try {
      const res = await apiBatchScrapeUrls(urls);
      setToastMessage(`Imported ${res.total_imported} of ${res.total_requested} songs!`);
      if (res.imported.length > 0) {
        setImportedSongs((prev) => {
          const updated = { ...prev };
          res.imported.forEach((song) => {
            const key = song.source_url || song.url;
            if (key) {
              updated[key] = song;
            }
          });
          return updated;
        });
        setSuccessSong(res.imported[res.imported.length - 1]);
        if (onSuccess) {
          res.imported.forEach((song) => onSuccess(song));
        }
      }
    } catch (err: any) {
      console.error("Batch import failed:", err);
      setError(err.message || "Batch import failed");
    } finally {
      setIsBatchImporting(false);
      setBatchProgress(null);
    }
  };

  const handleDirectScrape = () => {
    let cleanUrl = url.trim();
    if (!cleanUrl) return;
    if (!/^https?:\/\//i.test(cleanUrl)) {
      cleanUrl = "https://" + cleanUrl;
    }
    handleClose();
    navigate(`/import/${encodeURIComponent(cleanUrl)}`);
  };

  const isTab4U = (source: string) => source.toLowerCase().includes("tab4u");

  const renderSupportedSourcesInline = () => (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Supported Sources:
      </Typography>
      {SUPPORTED_DOMAINS.map((provider) => (
        <Chip
          key={provider.name}
          icon={<GlobeIcon fontSize="small" />}
          label={provider.name}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            borderColor: "#cbd5e1",
            bgcolor: "#ffffff",
            "& .MuiChip-icon": { color: provider.color },
          }}
        />
      ))}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              p: 1,
              boxShadow: 24,
              minHeight: 480,
              bgcolor: "#ffffff",
            },
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "primary.light",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DownloadIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Import Songs
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Search or import songs from Tab4U and Ultimate Guitar
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={isAnyImporting || isSearching || isBatchImporting}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="import chord modes"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              icon={<SearchIcon fontSize="small" />}
              iconPosition="start"
              label="Search by Song / Artist"
              sx={{ fontWeight: 600, textTransform: "none", minHeight: 48 }}
            />
            <Tab
              icon={<LinkIcon fontSize="small" />}
              iconPosition="start"
              label="Direct URL"
              sx={{ fontWeight: 600, textTransform: "none", minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {(isSearching || isAnyImporting || isBatchImporting) && (
          <LinearProgress sx={{ mx: 2, mt: 0.5, borderRadius: 1 }} />
        )}

        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Stack spacing={2.5}>
            {/* TAB 0: SEARCH BY SONG / ARTIST */}
            {tabIndex === 0 && (
              <Stack spacing={2}>
                <Box display="flex" gap={1}>
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Search Songs / Artists"
                    placeholder="Search song name or artist (e.g. Wonderwall, שלמה ארצי, Hotel California)..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (searchError) setSearchError(null);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    disabled={isSearching || isAnyImporting || isBatchImporting}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchQuery("");
                                setSearchError(null);
                              }}
                              disabled={isSearching || isAnyImporting || isBatchImporting}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                        sx: { borderRadius: 2 },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSearch()}
                    disabled={isSearching || isAnyImporting || isBatchImporting || !searchQuery.trim()}
                    sx={{
                      px: 3,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}
                    startIcon={
                      isSearching ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                  >
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </Box>

                {/* Supported Sources directly below input */}
                {renderSupportedSourcesInline()}

                {/* Search Error */}
                {searchError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {searchError}
                  </Alert>
                )}

                {/* Search Results List with Import All */}
                {hasSearched && !isSearching && searchResults.length > 0 && (
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                        Results ({searchResults.length} songs found)
                      </Typography>
                      {/* Import All Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={
                          isBatchImporting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <BatchImportIcon fontSize="small" />
                          )
                        }
                        disabled={isBatchImporting || isAnyImporting}
                        onClick={handleImportAll}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                          fontSize: "0.8rem",
                        }}
                      >
                        {isBatchImporting ? "Importing All..." : `Import All (${searchResults.length})`}
                      </Button>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        maxHeight: 340,
                        overflowY: "auto",
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                      }}
                    >
                      <List disablePadding>
                        {searchResults.map((item, index) => {
                          const isItemImporting = Boolean(importingUrls[item.url] || importingUrls[`https://${item.url}`]);
                          const importedSong = importedSongs[item.url] || importedSongs[`https://${item.url}`];
                          const isTab4uSource = isTab4U(item.source);
                          const targetSongId = item.existing_id || importedSong?.id;

                          return (
                            <React.Fragment key={`${item.url}-${index}`}>
                              {index > 0 && <Divider />}
                              <ListItem
                                sx={{
                                  px: 2,
                                  py: 1.25,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  transition: "background-color 0.15s",
                                  "&:hover": { bgcolor: "#f1f5f9" },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                      <Typography variant="body1" fontWeight={700}>
                                        {item.title}
                                      </Typography>
                                      <Chip
                                        label={item.source}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          bgcolor: isTab4uSource ? "#dbeafe" : "#fef3c7",
                                          color: isTab4uSource ? "#1e40af" : "#92400e",
                                          border: `1px solid ${isTab4uSource ? "#bfdbfe" : "#fde68a"}`,
                                        }}
                                      />
                                      {item.type && (
                                        <Chip
                                          label={item.type}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 500 }}
                                        />
                                      )}
                                      {item.version && item.version > 1 && (
                                        <Chip
                                          label={`v${item.version}`}
                                          size="small"
                                          sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#e2e8f0" }}
                                        />
                                      )}
                                      {item.rating && (
                                        <Box display="flex" alignItems="center" gap={0.3}>
                                          <StarIcon sx={{ fontSize: 14, color: "#eab308" }} />
                                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            {item.rating}
                                            {item.votes ? ` (${item.votes})` : ""}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Box display="flex" alignItems="center" gap={1} mt={0.3} flexWrap="wrap">
                                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        {item.artist || "Unknown Artist"}
                                      </Typography>
                                      <Tooltip title={item.url}>
                                        <Box
                                          component="a"
                                          href={item.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.3,
                                            color: "text.disabled",
                                            fontSize: "0.75rem",
                                            textDecoration: "none",
                                            "&:hover": { color: "primary.main", textDecoration: "underline" },
                                            maxWidth: { xs: 180, sm: 280, md: 350 },
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <OpenInNewIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                                          <span>{formatTruncatedUrl(item.url)}</span>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  }
                                  sx={{ mr: 2 }}
                                />

                                {targetSongId ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="success"
                                    onClick={() => {
                                      handleClose();
                                      navigate(`/Song?id=${targetSongId}`);
                                    }}
                                    startIcon={<OpenInNewIcon fontSize="small" />}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      px: 2,
                                      minWidth: 96,
                                    }}
                                  >
                                    Open
                                  </Button>
                                ) : (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    disabled={isItemImporting || isBatchImporting}
                                    onClick={() => handleImportSong(item.url)}
                                    startIcon={
                                      isItemImporting ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : (
                                        <DownloadIcon fontSize="small" />
                                      )
                                    }
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      px: 2,
                                      minWidth: 96,
                                      boxShadow: "none",
                                    }}
                                  >
                                    {isItemImporting ? "Importing" : "Import"}
                                  </Button>
                                )}
                              </ListItem>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Paper>
                  </Box>
                )}

                {/* Empty State */}
                {hasSearched && !isSearching && searchResults.length === 0 && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <MusicNoteIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                      No results found for "{searchQuery}"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Try checking the song or artist name, or switch to the <strong>Direct URL</strong> tab to paste the exact link.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}

            {/* TAB 1: DIRECT URL */}
            {tabIndex === 1 && (
              <Stack spacing={2}>
                <Box>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Song URL"
                    placeholder="https://www.tab4u.com/... or https://tabs.ultimate-guitar.com/..."
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value);
                      if (error) setError(null);
                    }}
                    onKeyDown={(e) => {
                      const clean = url.trim();
                      if (e.key === "Enter" && clean) {
                        handleDirectScrape();
                      }
                    }}
                    disabled={isBatchImporting}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: url ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setUrl("");
                                setError(null);
                              }}
                              disabled={Boolean(importingUrls[url.trim()] || importingUrls[`https://${url.trim()}`]) || isBatchImporting}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                        sx: { borderRadius: 2 },
                      },
                    }}
                  />

                  {/* Detected source badge or unknown source badge */}
                  {url.trim() && detectedSource && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Detected Source:
                      </Typography>
                      <Chip
                        icon={detectedSource.isKnown ? <GlobeIcon fontSize="small" /> : <UnknownSourceIcon fontSize="small" />}
                        label={detectedSource.name}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          color: detectedSource.color,
                          borderColor: detectedSource.color,
                          bgcolor: detectedSource.isKnown ? "#eff6ff" : "#f1f5f9",
                          border: "1px solid",
                        }}
                      />
                    </Box>
                  )}

                  {/* Supported Sources directly below input */}
                  {renderSupportedSourcesInline()}
                </Box>
              </Stack>
            )}

            {/* Scraping Progress Status */}
            {(isAnyImporting || isBatchImporting) && (
              <Fade in={Boolean(isAnyImporting || isBatchImporting)}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  p={1.5}
                  borderRadius={2}
                  bgcolor="#eff6ff"
                  border="1px solid #bfdbfe"
                >
                  <CircularProgress size={20} color="primary" />
                  <Typography variant="body2" color="primary.main" fontWeight={500}>
                    {isBatchImporting
                      ? "Batch importing chord sheets from web... Please wait."
                      : "Scraping chord sheet & metadata from web... Please wait a moment."}
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Error Message */}
            {error && !isAnyImporting && !isBatchImporting && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success Preview */}
            {successSong && !isAnyImporting && (
              <Alert
                icon={<SuccessIcon fontSize="inherit" />}
                severity="success"
                sx={{ borderRadius: 2, alignItems: "center" }}
                action={
                  successSong.id ? (
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        handleClose();
                        navigate(`/Song?id=${successSong.id}`);
                      }}
                      sx={{ fontWeight: 600, textTransform: "none" }}
                    >
                      Open Song
                    </Button>
                  ) : undefined
                }
              >
                Successfully imported <strong>{successSong.title}</strong> by{" "}
                <strong>{successSong.artist || "Unknown Artist"}</strong>!
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={isAnyImporting || isSearching || isBatchImporting}
            color="inherit"
            sx={{ fontWeight: 600, textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>

          {tabIndex === 1 && (
            <Button
              onClick={handleDirectScrape}
              disabled={isBatchImporting || !url.trim()}
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              sx={{
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
              }}
            >
              Import Song
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        message={toastMessage || ""}
      />
    </>
  );
}
